/**
 * AppSumo lifetime-deal tier matrix — the single source of truth for what an
 * AppSumo organization is entitled to (from the signed Launchpad/Select
 * Promotion Agreement, effective 2026-08-25).
 *
 * Client-safe by design: no keys(), no server-only, no database imports, so
 * plan cards and usage meters can render from it. Everything that ENFORCES a
 * limit projects these numbers into `organizationLimits` (monthly buckets)
 * and `creditPackBalances` (one-time buckets) via applyAppSumoEntitlements —
 * never read this table at a charge site.
 */

export type AppSumoTier = 1 | 2 | 3 | 4 | 5;

/** Days after in-app activation during which the avatar holdback applies. */
export const APPSUMO_REFUND_WINDOW_DAYS = 60;

/** Flat avatar credits per minute of clip for AppSumo orgs (contractual). */
export const APPSUMO_AVATAR_CREDITS_PER_MINUTE = {
  NORMAL: 1,
  STANDARD: 3,
  HIGH: 4
} as const;

export type AppSumoTierLimits = {
  tier: AppSumoTier;
  seats: number;
  /** Recycle on the activation anniversary. */
  monthly: {
    /** Upload minutes for Audio-only uploads. */
    uploadMinutes: number;
    /** Remotion Lambda renders; browser renders are unlimited. */
    cloudRenders: number;
  };
  /** Granted once for the lifetime of the license. */
  oneTime: {
    /** Auto / Both / Video-only uploads. */
    videoAnalysisMinutes: number;
    avatarMinutes: number;
    /** Portion of avatarMinutes usable inside the refund window. */
    refundWindowAvatarMinutes: number;
    digitalTwinTrainings: number;
    photoAvatarTrainings: number;
    translations: number;
  };
  maxVideoLengthSeconds: number;
  maxStorageBytes: number;
};

const GB = 1024 * 1024 * 1024;

export const APPSUMO_TIER_LIMITS: Record<AppSumoTier, AppSumoTierLimits> = {
  1: {
    tier: 1,
    seats: 1,
    monthly: { uploadMinutes: 15, cloudRenders: 5 },
    oneTime: {
      videoAnalysisMinutes: 30,
      avatarMinutes: 5,
      refundWindowAvatarMinutes: 2,
      digitalTwinTrainings: 0,
      photoAvatarTrainings: 1,
      translations: 5
    },
    maxVideoLengthSeconds: 15 * 60,
    maxStorageBytes: 5 * GB
  },
  2: {
    tier: 2,
    seats: 1,
    monthly: { uploadMinutes: 30, cloudRenders: 10 },
    oneTime: {
      videoAnalysisMinutes: 60,
      avatarMinutes: 12,
      refundWindowAvatarMinutes: 4,
      digitalTwinTrainings: 1,
      photoAvatarTrainings: 2,
      translations: 15
    },
    maxVideoLengthSeconds: 15 * 60,
    maxStorageBytes: 10 * GB
  },
  3: {
    tier: 3,
    seats: 3,
    monthly: { uploadMinutes: 60, cloudRenders: 20 },
    oneTime: {
      videoAnalysisMinutes: 120,
      avatarMinutes: 25,
      refundWindowAvatarMinutes: 6,
      digitalTwinTrainings: 2,
      photoAvatarTrainings: 4,
      translations: 30
    },
    maxVideoLengthSeconds: 20 * 60,
    maxStorageBytes: 15 * GB
  },
  4: {
    tier: 4,
    seats: 5,
    monthly: { uploadMinutes: 90, cloudRenders: 30 },
    oneTime: {
      videoAnalysisMinutes: 240,
      avatarMinutes: 45,
      refundWindowAvatarMinutes: 8,
      digitalTwinTrainings: 3,
      photoAvatarTrainings: 6,
      translations: 60
    },
    maxVideoLengthSeconds: 20 * 60,
    maxStorageBytes: 25 * GB
  },
  5: {
    tier: 5,
    seats: 10,
    monthly: { uploadMinutes: 120, cloudRenders: 40 },
    oneTime: {
      videoAnalysisMinutes: 400,
      avatarMinutes: 70,
      refundWindowAvatarMinutes: 10,
      digitalTwinTrainings: 4,
      photoAvatarTrainings: 10,
      translations: 100
    },
    maxVideoLengthSeconds: 25 * 60,
    maxStorageBytes: 40 * GB
  }
};

export function isAppSumoTier(value: unknown): value is AppSumoTier {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 5
  );
}

export function getAppSumoTierLimits(tier: number): AppSumoTierLimits {
  if (!isAppSumoTier(tier)) {
    throw new Error(`Unknown AppSumo tier: ${tier}`);
  }
  return APPSUMO_TIER_LIMITS[tier];
}

/** "Tier 3" — the plan name every badge and card shows next to the AppSumo pill. */
export function getAppSumoPlanLabel(tier: number | null | undefined): string {
  if (!isAppSumoTier(tier)) return 'AppSumo Lifetime';
  return `Tier ${tier}`;
}
